import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { brandingToCssVars, resolveBranding } from '@feed-widget/branding'
import { setBrandingPath } from '@feed-widget/branding/editor'
import { FeedCard } from '@feed-widget/components/card/FeedCard'
import { WinningTicketCard } from '@feed-widget/components/card/WinningTicketCard'
import { Button, StatePanel } from '../common'
import TokenField from '../design/TokenField'
import usePreviewInspect from '../design/usePreviewInspect'
import useInspectFocus from '../design/useInspectFocus'
import { PreviewInspectStatus, PreviewInspectToggle } from '../design/PreviewInspectControls'
import canvasStyles from '../design/SurfacePreviewCanvas.module.css'
import { WidgetPreviewProviders } from '../design/WidgetSurfacePreview'
import {
  CROWD_RECOMMENDATION_GROUP,
  PERSONALIZED_TICKET_GROUP,
  WINNING_TICKET_GROUP,
} from '../../config/brandingTokens'
import { getLocatablePaths } from '../../config/previewElementRegions'
import { labelForDisplayType } from '../../constants/displayTypes'
import { extractErrorMessage } from '../../lib/useApiMutation'
import { organizationApi } from '../../services/api'
import { GENERATED_POST_TYPES } from './generatedPostsPolicy'
import { CREATOR_TICKET_PREVIEW, GENERATED_PREVIEWS, SELECTIONS_TICKET_PREVIEW } from './generatedPostPreviews'
import styles from './GeneratedPostAppearanceForm.module.css'

const TOKEN_GROUPS = Object.freeze({
  'winning-ticket': WINNING_TICKET_GROUP,
  'crowd-recommendation': CROWD_RECOMMENDATION_GROUP,
  'personalized-ticket': PERSONALIZED_TICKET_GROUP,
})

const DESCRIPTIONS = Object.freeze({
  'winning-ticket': 'Style the winning ticket card. Saved changes appear in live feeds within a minute.',
  'crowd-recommendation': 'Style the crowd recommendation card. Empty fields keep the card\'s normal look. Saved changes appear in live feeds within a minute.',
  'personalized-ticket': 'Style the card for tickets built from users\' bets. Empty fields keep the card\'s normal look. Saved changes appear in live feeds within a minute.',
})

function readPath(document, path) {
  return path.split('.').reduce((value, key) => (value && typeof value === 'object' ? value[key] : undefined), document)
}

// Clearing a field returns it to the widget default rather than storing "".
function clearPath(document, path) {
  const parts = path.split('.')
  const parent = readPath(document, parts.slice(0, -1).join('.'))
  if (!parent || typeof parent !== 'object' || !(parts.at(-1) in parent)) return document
  const next = setBrandingPath(document, path, undefined)
  delete readPath(next, parts.slice(0, -1).join('.'))[parts.at(-1)]
  return next
}

function setField(document, path, value) {
  return value === '' || value === null || value === undefined
    ? clearPath(document || {}, path)
    : setBrandingPath(document || {}, path, value)
}

function tokenName(field) {
  return field.path.split('.').at(-1)
}

// Tokens of `group` whose value differs between two branding documents. A
// cleared token travels as '' so the server returns it to the widget default.
function changedTokens(group, saved, draft) {
  if (!saved || !draft) return {}
  return Object.fromEntries(group.fields
    .map((field) => [tokenName(field), readPath(saved, field.path) ?? '', readPath(draft, field.path) ?? ''])
    .filter(([, before, after]) => before !== after)
    .map(([token, , after]) => [token, after]))
}

// `document` with the given group tokens applied on top.
function withTokens(group, document, tokens) {
  return group.fields.reduce(
    (next, field) => (Object.hasOwn(tokens, tokenName(field)) ? setField(next, field.path, tokens[tokenName(field)]) : next),
    document,
  )
}

function noop() {}

// Edits one kind's branding group for every org admin, clients included. It
// reads the whole document to preview the card in the org's own look and saves
// through the scoped endpoint, which accepts nothing outside that group.
export default function GeneratedPostAppearanceForm({
  kind,
  // False while the policy form shows another type (every editor stays
  // mounted): edit mode ends and its selection is dropped.
  active = true,
  orgId,
  onDirtyChange,
  headingId = `${kind}-appearance-heading`,
  headingLevel = 4,
}) {
  const type = GENERATED_POST_TYPES.find((candidate) => candidate.kind === kind)
  const group = TOKEN_GROUPS[kind]
  if (!type || !group) throw new Error(`Unknown generated post kind: ${kind}`)

  const queryClient = useQueryClient()
  // Kept together so a refetch can re-apply this group's pending edits.
  const [documents, setDocuments] = useState({ saved: null, draft: null })
  const { saved, draft } = documents
  const [operationError, setOperationError] = useState('')

  const brandingQuery = useQuery({
    queryKey: ['branding', orgId],
    queryFn: () => organizationApi.getBranding(orgId),
    enabled: Boolean(orgId),
  })

  // The query is shared with the other kinds' editors; a save by one refetches
  // for all, so this group's pending edits go back on top.
  useEffect(() => {
    if (!brandingQuery.data) return
    setDocuments((current) => ({
      saved: brandingQuery.data,
      draft: withTokens(group, brandingQuery.data, changedTokens(group, current.saved, current.draft)),
    }))
  }, [brandingQuery.data, group])

  const pendingTokens = changedTokens(group, saved, draft)
  const dirty = Object.keys(pendingTokens).length > 0

  useEffect(() => {
    onDirtyChange?.(dirty)
    return () => onDirtyChange?.(false)
  }, [dirty, onDirtyChange])

  const resolved = useMemo(() => resolveBranding(draft || {}), [draft])
  const cssVars = useMemo(() => brandingToCssVars(resolved), [resolved])
  const defaults = useMemo(() => resolveBranding({}), [])

  const saveMutation = useMutation({
    mutationFn: ({ tokens }) => organizationApi.updateGeneratedPostBranding(orgId, kind, tokens),
    onMutate: () => setOperationError(''),
    onSuccess: (_response, { tokens }) => {
      // Only what was sent is saved; edits made while the request was in
      // flight stay pending.
      setDocuments((current) => ({ saved: withTokens(group, current.saved, tokens), draft: current.draft }))
      toast.success(`${type.label} appearance saved`)
      queryClient.invalidateQueries({ queryKey: ['branding', orgId] })
    },
    onError: (error) => setOperationError(extractErrorMessage(error, `${type.label} appearance could not be saved.`)),
  })

  const updateField = (path, value) => setDocuments((current) => ({ ...current, draft: setField(current.draft, path, value) }))

  // Click-to-edit, as on the Surfaces canvas: a click in the preview focuses
  // the token field, and a field's Locate flashes its elements.
  const frameRef = useRef(null)
  const inspectorRef = useRef(null)
  const groupPaths = useMemo(() => new Set(group.fields.map((field) => field.path)), [group])
  const [focusRequest, setFocusRequest] = useState(null) // { label, paths, nonce }
  const [flashRequest, setFlashRequest] = useState(null) // { path, nonce }
  const handleTokenSelect = useCallback((selection) => {
    setFocusRequest((previous) => (selection
      ? { label: selection.label, paths: selection.paths, nonce: (previous?.nonce || 0) + 1 }
      : null))
  }, [])
  const { inspectMode, toggleInspectMode, inspectStatus, flashMiss, frameProps } = usePreviewInspect({
    frameRef,
    activePaths: groupPaths,
    onTokenSelect: handleTokenSelect,
    flashRequest,
    resetKey: `${kind}:${active}`,
  })
  // A hidden editor must not keep edit mode (and its key suppression) on.
  useEffect(() => {
    if (!active && inspectMode) toggleInspectMode()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active])
  const highlightSet = useInspectFocus(focusRequest, groupPaths, noop, inspectorRef)
  const locatablePaths = getLocatablePaths()
  const locateFor = (path) => (locatablePaths.has(path)
    ? () => setFlashRequest((previous) => ({ path, nonce: (previous?.nonce || 0) + 1 }))
    : undefined)

  const Heading = `h${headingLevel}`
  const previewItems = kind === 'winning-ticket'
    ? [CREATOR_TICKET_PREVIEW, SELECTIONS_TICKET_PREVIEW]
    : [GENERATED_PREVIEWS[kind]]

  // Canvas | inspector, laid out like the Surfaces workspace: the preview on
  // the shared dark canvas, the save state and token rows in the inspector.
  return (
    <section className={styles.workspace} aria-labelledby={headingId}>
      <div className={[canvasStyles.canvas, styles.canvas].join(' ')}>
        <div className={canvasStyles.canvasToolbar}>
          <PreviewInspectToggle inspectMode={inspectMode} onToggle={toggleInspectMode} />
        </div>
        <PreviewInspectStatus
          inspectMode={inspectMode}
          inspectStatus={inspectStatus}
          flashMiss={flashMiss}
          idleText={`${labelForDisplayType('story_block')} · ${type.label} preview`}
        />
        <div className={[canvasStyles.frameScroll, styles.frameScroll].join(' ')}>
          {draft ? (
            <div
              ref={frameRef}
              className={[canvasStyles.frame, styles.frame, inspectMode ? canvasStyles.frameInspecting : ''].filter(Boolean).join(' ')}
              role="region"
              aria-label={`${type.label} preview`}
              style={cssVars}
              data-dark-mode={resolved.darkMode ? 'true' : 'false'}
              data-testid={`${kind}-preview`}
              {...frameProps}
            >
              <WidgetPreviewProviders
                branding={resolved}
                items={previewItems}
                showBettingChrome={kind !== 'winning-ticket'}
              >
                {kind === 'winning-ticket' ? (
                  <>
                    <p className={styles.previewLabel}>From a creator&apos;s post</p>
                    <WinningTicketCard
                      item={CREATOR_TICKET_PREVIEW}
                      index={0}
                      viewerIdentified
                      isFollowing={() => false}
                      isFollowPending={() => false}
                      onToggleFollow={noop}
                    />
                    <p className={styles.previewLabel}>From settled selections</p>
                    <WinningTicketCard
                      item={SELECTIONS_TICKET_PREVIEW}
                      index={1}
                      viewerIdentified
                      isFollowing={() => false}
                      isFollowPending={() => false}
                      onToggleFollow={noop}
                    />
                  </>
                ) : (
                  // FeedCard applies the origin's tokens and badge itself.
                  <FeedCard item={GENERATED_PREVIEWS[kind]} index={0} darkModeOverride={resolved.darkMode} />
                )}
              </WidgetPreviewProviders>
            </div>
          ) : null}
        </div>
      </div>

      <aside ref={inspectorRef} className={styles.inspector} aria-label={`${type.label} appearance inspector`}>
        {draft ? (
          <div className={styles.inspectorHeader}>
            <span role="status" className={styles.saveState}>
              {dirty ? 'Unsaved appearance changes' : 'Appearance is saved'}
            </span>
            <Button
              type="button"
              size="sm"
              disabled={!dirty}
              loading={saveMutation.isPending}
              onClick={() => saveMutation.mutate({ tokens: pendingTokens })}
            >
              Save appearance
            </Button>
          </div>
        ) : null}
        <div className={styles.inspectorIntro}>
          <div className={styles.titleRow}>
            <Heading id={headingId} className={styles.title}>{type.label} appearance</Heading>
            {draft ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={!dirty || saveMutation.isPending}
                onClick={() => {
                  setDocuments((current) => ({ ...current, draft: current.saved }))
                  setOperationError('')
                }}
              >
                Reset changes
              </Button>
            ) : null}
          </div>
          <p className={styles.description}>{DESCRIPTIONS[kind]}</p>
        </div>

        {brandingQuery.isLoading ? (
          <StatePanel compact state="loading" title="Loading appearance…" />
        ) : brandingQuery.isError ? (
          <StatePanel
            compact
            state="error"
            title="Appearance could not load"
            description={extractErrorMessage(brandingQuery.error, `${type.label} styling is unavailable.`)}
            onRetry={brandingQuery.refetch}
          />
        ) : draft ? (
          <>
            {operationError ? <StatePanel compact state="error" title="Action failed" description={operationError} /> : null}
            <div className={styles.fieldList}>
              {group.fields.map((field) => (
                <TokenField
                  key={field.path}
                  path={field.path}
                  label={field.label}
                  kind={field.kind}
                  inheritanceMode="canonical"
                  value={readPath(draft, field.path)}
                  inheritedValue={readPath(defaults, field.path)}
                  placeholder={field.placeholder ?? (field.path === 'components.winningTicket.feedCreditName' ? 'Your feed name, e.g. Banter' : undefined)}
                  onChange={(next) => updateField(field.path, next)}
                  onReset={() => updateField(field.path, '')}
                  inspectFocus={highlightSet.has(field.path)}
                  onLocate={locateFor(field.path)}
                />
              ))}
            </div>
          </>
        ) : null}
      </aside>
    </section>
  )
}
